import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { db, auth } from "./firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import ProfileStats from "./components/profile/ProfileStats";
import PublicBuildCard from "./components/profile/PublicBuildCard";
import ProfileSocialLinks from "./components/profile/ProfileSocialLinks";
import BuilderDnaCard from "./components/profile/BuilderDnaCard";
import { calculateBuilderDna } from "./utils/builderDna";
import PageLayout from "./components/PageLayout";
import {
  FiSettings,
  FiUser,
  FiShoppingBag,
  FiImage,
  FiTag,
  FiInfo,
  FiMail,
  FiLogOut,
  FiBookmark,
} from "react-icons/fi";
import BottomSheet from "./components/BottomSheet";
import Username from "./components/Username";
import { isMerchAdminUser } from "./utils/merchAdmin";


function ProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
const [loggedInUsername, setLoggedInUsername] = useState("");


  const [profile, setProfile] = useState(null);
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
const [profileError, setProfileError] = useState("");

  useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (!user) {
      setLoggedInUsername("");
      return;
    }

    const snapshot = await getDocs(collection(db, "profiles"));

    snapshot.forEach((docItem) => {
      const data = docItem.data();

if (
  data.userId === user.uid ||
  data.uid === user.uid ||
  data.email === user.email
) {
  setLoggedInUsername(data.username || docItem.id);
}
    });
  });

  return () => unsubscribe();
}, []);

  useEffect(() => {
  const getProfile = async () => {
    try {
      setLoadingProfile(true);
      setProfileError("");

      if (!username) {
        setProfileError("Missing username.");
        return;
      }

      const docRef = doc(db, "profiles", username.toLowerCase());
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setProfile(docSnap.data());
      } else {
        setProfileError("Profile not found");
      }
    } catch (error) {
      setProfileError(error.message);
    } finally {
      setLoadingProfile(false);
    }
  };

  getProfile();
}, [username]);


  const isOwnProfile =
  loggedInUsername &&
  username &&
  loggedInUsername.toLowerCase() === username.toLowerCase();


const handleLogout = async () => {
  try {
    await signOut(auth);
    setSettingsOpen(false);
    navigate("/");
  } catch (error) {
    alert(error.message);
  }
};

const isMerchAdmin = isMerchAdminUser({
  user: auth.currentUser,
  profile: { username: loggedInUsername }
});
const builderDna = useMemo(() => calculateBuilderDna(profile?.garageCars || []), [profile]);


if (loadingProfile) {
  return (
    <PageLayout maxWidth="760px">
      <div style={notFoundStyle}>Loading profile...</div>
    </PageLayout>
  );
}

if (profileError || !profile) {
  return (
    <PageLayout maxWidth="500px">
      <div style={notFoundStyle}>
        {profileError || "Profile not found"}
      </div>
    </PageLayout>
  );
}

return (
  <>
    <PageLayout maxWidth="760px">
      <ProfilePageStyles />
      <div style={cardStyle}>
{isOwnProfile && (
  <button
    type="button"
    aria-label="Profile settings"
    style={settingsButtonStyle}
    onClick={() => setSettingsOpen(true)}
  >
    <FiSettings />
  </button>
)}

          <div style={profileContentStyle}>
            <div style={profileHeaderStyle}>
              {profile.imageUrl ? (
                <img
                  src={profile.imageUrl}
                  alt={`${profile.displayName || profile.username} avatar`}
                  onClick={() => setSelectedImage(profile.imageUrl)}
                  style={profileImageStyle}
                />
              ) : (
                <div style={profileImagePlaceholderStyle}>
                  {profile.username?.charAt(0)?.toUpperCase()}
                </div>
              )}

              <div style={profileTextStyle}>
                <h1 style={profileNameStyle}>
                  {profile.displayName || profile.username}
                </h1>

                <p style={{ opacity: 0.7, margin: "4px 0" }}><Username username={profile.username} profile={profile} /></p>

                <p style={{ margin: "4px 0" }}>{profile.location}</p>

              </div>
            </div>

            {profile.bio && (
  <p style={bioStyle}>
    {profile.bio}
  </p>
)}

<ProfileStats profile={profile} />
<BuilderDnaCard builderDna={builderDna} isOwner={isOwnProfile} />

            <section style={garageSectionStyle} aria-labelledby="profile-garage-title">
              <h2 id="profile-garage-title" className="mybuildid-section-heading" style={sectionTitleStyle}>Garage</h2>
              {profile.garageCars?.length > 0 ? (
                <div style={garageGridStyle}>{profile.garageCars.map((car) => <PublicBuildCard key={car.id} car={car} username={profile.username || username} />)}</div>
              ) : (
                <div style={emptyGarageStyle}><h3>No builds yet</h3><p>This user has not published a vehicle build.</p>{isOwnProfile && <Link to="/garage">Add your first vehicle</Link>}</div>
              )}
            </section>

            <ProfileSocialLinks profile={profile} />
          </div>
      </div>
    </PageLayout>

<BottomSheet open={settingsOpen} onClose={() => setSettingsOpen(false)}>
  <Link
    to="/account"
    style={sheetItemStyle}
    onClick={() => setSettingsOpen(false)}
  >
    <FiUser /> Account
  </Link>

  <Link
    to="/merch"
    style={sheetItemStyle}
    onClick={() => setSettingsOpen(false)}
  >
    <FiShoppingBag /> Merch Engine
  </Link>

  {isMerchAdmin && (
    <Link
      to="/merch/artwork-preview"
      style={sheetItemStyle}
      onClick={() => setSettingsOpen(false)}
    >
      <FiImage /> Artwork Preview
    </Link>
  )}

  <Link
    to="/for-sale"
    style={sheetItemStyle}
    onClick={() => setSettingsOpen(false)}
  >
    <FiTag /> For Sale
  </Link>

<Link
  to="/saved"
  style={sheetItemStyle}
  onClick={() => setSettingsOpen(false)}
>
  <FiBookmark /> Saved Builds
</Link>

  <Link
    to="/about"
    style={sheetItemStyle}
    onClick={() => setSettingsOpen(false)}
  >
    <FiInfo /> About
  </Link>

  <Link
    to="/contact"
    style={sheetItemStyle}
    onClick={() => setSettingsOpen(false)}
  >
    <FiMail /> Contact
  </Link>

  <button style={logoutSheetButtonStyle} onClick={handleLogout}>
    <FiLogOut /> Logout
  </button>
</BottomSheet>

      {selectedImage && (
        <div style={modalStyle} onClick={() => setSelectedImage(null)}>
          <button style={closeButtonStyle}>Close</button>

          <img src={selectedImage} alt="Expanded" style={expandedImageStyle} />
        </div>
      )}
    </>
  );
}

const notFoundStyle = {
  backgroundColor: "#111",
  color: "white",
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontFamily: "Arial",
};


const cardStyle = {
  width: "100%",
  maxWidth: "760px",
  margin: "0 auto",
  backgroundColor: "#181818",
  border: "1px solid #242424",
  borderRadius: "20px",
  overflow: "hidden",
  position: "relative",
};

const settingsButtonStyle = {
  position: "absolute",
  top: "16px",
  right: "16px",
  width: "42px",
  height: "42px",
  borderRadius: "999px",
  border: "1px solid #2a2a2a",
  backgroundColor: "#111",
  color: "white",
  fontSize: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 2,
};

const profileContentStyle = {
  padding: "24px",
  marginTop: "0px",
};

const profileImageStyle = {
  width: "88px",
  height: "88px",
  borderRadius: "50%",
  objectFit: "cover",
  border: "5px solid #181818",
  cursor: "pointer",
};

const bioStyle = {
  marginTop: "22px",
  marginBottom: 0,
  lineHeight: "1.6",
  opacity: 0.9,
};

const garageGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,340px),1fr))",
  gap: "14px"
};

const garageSectionStyle = { marginTop: "26px" };
const sectionTitleStyle = { margin: "0 0 16px", fontSize: "22px" };
const emptyGarageStyle = { display: "grid", gap: "8px", padding: "22px", border: "1px solid #303030", borderRadius: "14px", background: "#1d1d1d", textAlign: "center" };

const modalStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.9)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  padding: "20px",
};

const expandedImageStyle = {
  maxWidth: "90%",
  maxHeight: "85%",
  objectFit: "contain",
  borderRadius: "12px",
};

const closeButtonStyle = {
  position: "absolute",
  top: "20px",
  right: "20px",
  padding: "10px 16px",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "bold",
};

const profileHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  position: "relative",
  flexWrap: "wrap"
};

const profileTextStyle = {
  flex: "1 1 180px",
  minWidth: 0,
  paddingRight: "56px"
};

const profileNameStyle = {
  margin: 0,
  fontSize: "32px",
  lineHeight: "1.05",
  wordBreak: "normal",
  overflowWrap: "break-word",
  hyphens: "none"
};

const profileImagePlaceholderStyle = {
  width: "88px",
  height: "88px",
  borderRadius: "50%",
  backgroundColor: "#242424",
  border: "5px solid #181818",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "42px",
  fontWeight: "bold",
  color: "#e10600",
};

const sheetItemStyle = {
  color: "white",
  textDecoration: "none",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "16px 4px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  fontWeight: "700",
  fontSize: "16px",
};

const logoutSheetButtonStyle = {
  ...sheetItemStyle,
  width: "100%",
  backgroundColor: "transparent",
  border: "none",
  color: "#e10600",
  cursor: "pointer",
  fontFamily: "inherit",
};


const animationStyles = document.createElement("style");
animationStyles.innerHTML = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

if (!document.head.querySelector("[data-profile-sheet-animations]")) {
  animationStyles.setAttribute("data-profile-sheet-animations", "true");
  document.head.appendChild(animationStyles);
}

function ProfilePageStyles() {
  return <style>{`
    .public-build-card{display:block;min-width:0;overflow:hidden;border:1px solid #303030;border-radius:16px;background:#1b1b1b;color:#fff;text-decoration:none;box-shadow:0 14px 34px rgba(0,0,0,.2);transition:border-color .2s ease,transform .2s ease}
    .mybuildid-section-heading{color:#e10600;font-weight:900;line-height:1.2;letter-spacing:0}
    .public-build-card:hover{border-color:#484848;transform:translateY(-2px)}
    .public-build-card:focus-visible,.profile-socials a:focus-visible{outline:3px solid rgba(225,6,0,.55);outline-offset:3px}
    .public-build-card__cover{width:100%;aspect-ratio:16/9;overflow:hidden;background:#101010}
    .public-build-card__cover img{display:block;width:100%;height:100%;object-fit:cover}
    .public-build-card__placeholder{display:flex;width:100%;height:100%;align-items:center;justify-content:center;flex-direction:column;gap:7px;color:#777;font-size:12px;font-weight:800}
    .public-build-card__placeholder span{color:#e10600;font-size:32px}
    .public-build-card__content{display:grid;gap:12px;padding:16px}
    .public-build-card__heading{display:flex;min-width:0;align-items:flex-start;justify-content:space-between;gap:10px}
    .public-build-card__heading h3{display:-webkit-box;min-width:0;margin:0;overflow:hidden;font-size:clamp(19px,4vw,23px);line-height:1.2;-webkit-box-orient:vertical;-webkit-line-clamp:2}
    .public-build-card__heading span{max-width:42%;overflow:hidden;flex:0 0 auto;padding:6px 8px;border:1px solid #454545;border-radius:999px;color:#aaa;font-size:9px;font-weight:900;letter-spacing:.7px;text-overflow:ellipsis;white-space:nowrap}
    .public-build-card__content p{display:-webkit-box;margin:0;overflow:hidden;color:#aaa;font-size:14px;line-height:1.55;-webkit-box-orient:vertical;-webkit-line-clamp:3}
    .public-build-card__content strong{display:flex;align-items:center;gap:7px;color:#fff;font-size:13px}
    .public-build-card__content strong svg{color:#e10600}
    .profile-socials{margin-top:26px}.profile-socials h2{margin:0 0 14px;font-size:22px}.profile-socials>div{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px}
    .profile-socials a{display:flex;min-width:0;min-height:46px;align-items:center;gap:10px;padding:10px 13px;border:1px solid #303030;border-radius:11px;background:#1d1d1d;color:#fff;font-weight:800;text-decoration:none;box-sizing:border-box}
    .profile-socials a svg{flex:0 0 auto;color:#e10600;font-size:18px}
    .builder-dna-card{width:100%;margin-top:18px;padding:18px;border:1px solid #303030;border-radius:15px;background:#1d1d1d;box-sizing:border-box}
    .builder-dna-card h2{margin:0 0 8px;font-size:19px}.builder-dna-label{display:block;font-size:15px}.builder-dna-bar{display:flex;width:100%;height:9px;margin:15px 0 13px;overflow:hidden;border-radius:999px;background:#292929}.builder-dna-bar span{display:block;height:100%}.builder-dna-list{display:grid;gap:7px}.builder-dna-list>div{display:flex;align-items:center;justify-content:space-between;gap:14px;color:#aaa;font-size:13px}.builder-dna-list strong{color:#fff}.builder-dna-context,.builder-dna-empty{margin:13px 0 0;color:#7f7f7f;font-size:11px;line-height:1.5}
    @media(max-width:560px){.public-build-card:hover{transform:none}.profile-socials>div{grid-template-columns:1fr}}
  `}</style>;
}

export default ProfilePage;
